import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import utils from '../utils';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';

import Clef from '../uTheory.Notation/clef';
import Chord from '../uTheory.Notation/chord';
import KeySignature from '../uTheory.Notation/keySignature';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import type { EditableOptions } from '../uTheory.Notation/editable';
import Editable from '../uTheory.Notation/editable';
import Music from '../uTheory.Notation/music';
import Note from '../uTheory.Notation/note';

interface ChordSpellingStaffOptions extends InteractiveOptions {
  triadOrSeventh: 'triads' | 'sevenths';
}

export default class ChordSpellingStaff extends InteractivePrototype {
  declare options: ChordSpellingStaffOptions;

  skillsCalculator: SkillCalculator;

  noteHolder: NoteHolder;

  keySig: KeySignature;

  clef: Clef;

  staff: Staff;

  chord: Chord;

  measure: Measure;

  voice: Voice;

  maxQuestions: number;

  declare questions: {
    question: string;
    answer: string;
    chord: Chord;
    skills: string[];
    text: number;
  }[];

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: ChordSpellingStaffOptions) {
    super(displayDiv, options);
    this.settings = {
      instructions: true,
      editor: {
        fixedWidth: 180,
        ariaLive: 'assertive',
        ariaTitle: {
          keySignature: false,
          rhythm: false,
          barline: false,
        },
      },
      notation: false,
      letters: {
        trashButton: true,
        checkButton: true,
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.targetSpeedFromSkills =
      this.options.triadOrSeventh !== 'sevenths' ? ['spelling_triads'] : ['seventh_chord_spelling'];
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: ChordSpellingStaffOptions) {
    // this.answer = { noteHolder: {} };
    this.skillsCalculator = new SkillCalculator();
    return super.init(displayDiv, options);
  }

  initializeMusic() {
    const keySig = new KeySignature('C', 'Major');
    const clef = new Clef('treble');
    const staff = new Staff(clef);
    const chord = new Chord('C4', 'M', 0);
    const noteHolderOptions: Partial<EditableOptions> = {
      deleteable: ['ghost'],
      pitch: true,
      selected: true,
      selectable: true,
      maxNotes: 4,
    };

    this.noteHolder = new NoteHolder('', 'w');
    // this.noteHolder.ghostIfEmpty = true; No atribute ghostIfEmpty
    this.noteHolder.editable = new Editable(this.noteHolder, noteHolderOptions);
    const measure = new Measure(this.noteHolder);
    const voice = new Voice(measure);
    voice.setStaff(staff);

    this.keySig = keySig;
    this.clef = clef;
    this.staff = staff;
    this.chord = chord;
    this.measure = measure;
    this.voice = voice;
    this.music = new Music(staff, voice, keySig);
  }

  generateQuestions() {
    const options = this.options;
    this.questions = this.getQuestions(options);
  }

  getQuestions(options: ChordSpellingStaffOptions) {
    const drill = options.drill !== undefined && options.drill;
    return drill ? this.generateDrillQuestions(options) : this.generateManualQuestions(options);
  }

  generateManualQuestions(options: ChordSpellingStaffOptions) {
    if (!utils.isArray(options.questions))
      throw new Error(
        'An array must be provided in options.questions for non-drill initialization.'
      );
    return this.processQuestions(options.questions, options);
  }

  generateDrillQuestions(options: ChordSpellingStaffOptions) {
    let questions = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    const maxQuestions = options.maxQuestions || 5;

    const randomOctave = () => [2, 3, 4][Math.floor(Math.random() * 3)];
    const qg = new QuestionGenerator();
    questions = qg
      .rejectDuplicates(questions, maxQuestions)
      .map((question) => `${question}${randomOctave()}`);
    return this.processQuestions(questions, options);
  }

  processQuestions(questions: string[], options: ChordSpellingStaffOptions) {
    return questions.map((question, index) => {
      const root = Note.fromStringOrNote(question);
      const quality =
        options.triadOrSeventh === 'triads'
          ? 'M'
          : options.triadOrSeventh === 'sevenths'
          ? 'M7'
          : 'M';
      const chord = new Chord(root, quality, 0);
      let answer = chord.root.letter + chord.third.letter + chord.fifth.letter;

      if (options.triadOrSeventh === 'sevenths') {
        answer += chord.seventh.letter;
      }

      return {
        question: root.letter,
        answer,
        chord,
        skills:
          this.options.triadOrSeventh !== 'sevenths'
            ? ['spelling_triads']
            : ['seventh_chord_spelling'],
        text: index + 1,
      };
    });
  }

  calculateSkill(chord: Chord) {
    return this.skillsCalculator.getChordSkill(chord);
  }

  newQuestion() {
    this.progressTracker.start(this.targetSpeed);

    const chord = this.questions[this.questionNumber].chord;
    const root = chord.root;
    const quality = chord.quality;
    const inversion = chord.inversion;

    this.chord.set(root, quality, inversion);
    this.questionPrompt = this.getQuestionPrompt();
    const clefs = chord.noteHolder.prefersClef();
    if (clefs.length === 0) {
      console.warn('That question requires a grand staff. Advancing to next question.');
      console.warn(`${chord.noteHolder}`);
      this.rightAnswer();
      return;
    }
    const clef = clefs[Math.floor(Math.random() * clefs.length)];
    this.staff.setClef(clef);

    this.noteHolder.length = 0;
    this.noteHolder.editable.selected = false;
    this.noteHolder.notes[0] = root;
    this.noteHolder.editable.options.fixedNotes = [root];
    this.editor.renderer.render();

    super.newQuestion();
  }

  getQuestionPrompt() {
    let questionPrompt: string;
    const chord = this.questions[this.questionNumber].chord;
    const triadOrSeventh = chord.isSeventh === true ? 'seventh chord' : 'triad';
    const testMode = this.optionsTrue('testMode');

    if (testMode) {
      questionPrompt = `Write a ${triadOrSeventh} above the given note.`;
    } else {
      questionPrompt = `Write a ${triadOrSeventh} above the given note.<br/> Question #{question} of ${this.questions.length}`;
    }

    return questionPrompt;
  }

  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      const root = `${questionString}4`;
      const quality =
        this.options.triadOrSeventh === 'triads'
          ? 'M'
          : this.options.triadOrSeventh === 'sevenths'
          ? 'M7'
          : 'M';
      const chord = new Chord(root as NoteName, quality, 0);
      let answer = chord.root.letter + chord.third.letter + chord.fifth.letter;
      const questions = [];

      if (this.options.triadOrSeventh === 'sevenths') {
        answer += chord.seventh.letter;
      }

      questions.push({
        question: questionString,
        answer,
        chord,
      });

      this.questions = questions;

      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners() {
    this.letterPanel.addCustomButtonCallback(this.editor.delete, this.editor);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  private getUserAnswer() {
    let answer = '';
    this.noteHolder
      .clone()
      .sortAndCompact()
      .notes.forEach((note) => {
        answer += note.letter;
      });
    return answer;
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return;
    const testMode = this.optionsTrue('testMode');
    const questionAnswer = this.questions[this.questionNumber].answer;
    const userAnswer = this.getUserAnswer();
    const correct = questionAnswer === userAnswer;

    if (testMode) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.getAnswerAndData(true);
      this.rightAnswer(1000);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  getAnswerAndData(correct) {
    const correctAnswer = this.questions[this.questionNumber].answer;
    const studentAnswerText = this.noteHolder.notes.reduce((prev, curr) => prev + curr.letter, ''); // Create text from student's answer
    const answerAndData = {
      question: this.questions[this.questionNumber].question,
      questionType: 'ChordSpellingStaff',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: this.editor.renderer.exportSVG(),
      studentAnswerText,
      correctAnswerText: `${correctAnswer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.questions[this.questionNumber].skills,
    };

    return answerAndData;
  }

  playAnswer() {
    this.player.play(this.noteHolder, { startTime: 0, stopTime: 1.2, velocity: 0.7 });
  }

  gameOverCount = 0;

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    try {
      this.displayDiv.removeChild(this.lettersDiv);
    } catch (e) {
      // Swallow it.
    }
    super.gameOver();
  }

  static registry = [
    {
      description: 'White Note Triads on Staff',
      skills: ['spelling_triads'],
      options: {
        triadOrSeventh: 'triads',
      },
      maxQuestions: 5,
    },
    {
      description: 'White Note Seventh Chords on Staff',
      skills: ['seventh_chord_spelling'],
      options: {
        triadOrSeventh: 'sevenths',
      },
      maxQuestions: 5,
    },
  ];
}
